/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

/**
 * Custom React hook for AI System execution via WebSocket.
 * 
 * Replaces HTTP polling with real-time WebSocket streaming for:
 * - Starting AI System executions
 * - Receiving expert progress updates
 * - Getting execution completion notifications
 * 
 * Benefits over polling:
 * - ~90% reduction in HTTP requests
 * - Instant updates (vs 1s polling delay)
 * - Lower bandwidth usage
 * - Better scalability
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { authService } from '../services/auth';
import type {
  AISystemExecution,
} from '../types';

// Execution status type
type AISystemExecutionStatus = 'PENDING' | 'START' | 'RUNNING' | 'SUCCESS' | 'COMPLETED' | 'FAILED' | 'ERROR' | 'CANCELLED' | 'CANCELED' | 'TIMEOUT';

// WebSocket message types for AI System execution
export interface AISystemWSMessage {
  type: 
    | 'connected'
    | 'execution_started'
    | 'expert_started'
    | 'expert_completed'
    | 'expert_skipped'
    | 'execution_progress'
    | 'execution_completed'
    | 'execution_error'
    | 'execution_status'
    | 'stop_requested'
    | 'subscribed'
    | 'unsubscribed'
    | 'pong'
    | 'error';
  system_id?: number;
  system_name?: string;
  execution_id?: string;
  expert_id?: number;
  expert_name?: string;
  expert_type?: string;
  status?: string;
  result?: Record<string, any>;
  total_cost?: number;
  progress?: number;
  experts_completed?: number;
  experts_total?: number;
  error?: string;
  message?: string;
  reason?: string;
  action?: string;
  // For execution_status
  created_at?: string;
  updated_at?: string;
  completed_at?: string;
  expert_results?: Array<{
    expert_id: number;
    expert_name: string;
    expert_type: string;
    status: string;
    total_cost: number;
  }>;
  metadata?: Record<string, any>;
  logs?: string;
}

export interface ExpertProgress {
  expert_id: number;
  expert_name: string;
  expert_type: string;
  status: 'pending' | 'running' | 'completed' | 'skipped' | 'error';
  total_cost?: number;
  result?: Record<string, any>;
}

export interface UseAISystemSocketOptions {
  /** AI System ID to connect to */
  systemId: number;
  /** Called when connection is established */
  onConnect?: () => void;
  /** Called when connection is closed */
  onDisconnect?: () => void;
  /** Called when any message is received */
  onMessage?: (message: AISystemWSMessage) => void;
  /** Called when execution starts */
  onExecutionStarted?: (executionId: string) => void;
  /** Called when an expert starts */
  onExpertStarted?: (expertId: number, expertName: string) => void;
  /** Called when an expert completes */
  onExpertCompleted?: (expertId: number, expertName: string, status: string, result?: any) => void;
  /** Called with progress updates */
  onProgress?: (progress: number, expertsCompleted: number, expertsTotal: number) => void;
  /** Called when execution completes successfully */
  onExecutionCompleted?: (executionId: string, status: string, result?: any, totalCost?: number) => void;
  /** Called when execution fails */
  onExecutionError?: (executionId: string, error: string) => void;
  /** Auto-connect on mount (default: true) */
  autoConnect?: boolean;
}

export interface UseAISystemSocketReturn {
  /** Whether WebSocket is connected */
  isConnected: boolean;
  /** Whether an execution is currently running */
  isExecuting: boolean;
  /** Current execution ID (if any) */
  currentExecutionId: string | null;
  /** Current execution status */
  executionStatus: string | null;
  /** Progress percentage (0-100) */
  progress: number;
  /** Expert progress tracking */
  expertProgress: ExpertProgress[];
  /** Last error message */
  error: string | null;
  /** Total cost of current execution */
  totalCost: number;
  /** Start a new execution */
  startExecution: (metadata?: Record<string, any>, executionType?: string) => void;
  /** Stop the current execution */
  stopExecution: () => void;
  /** Subscribe to an existing execution */
  subscribeToExecution: (executionId: string) => void;
  /** Get current status of an execution */
  getExecutionStatus: (executionId: string) => void;
  /** Manually connect to WebSocket */
  connect: () => void;
  /** Manually disconnect from WebSocket */
  disconnect: () => void;
}

const normalizeExpertStatus = (status?: string): ExpertProgress['status'] => {
  const normalized = (status || '').toLowerCase();

  if (['success', 'complete', 'completed', 'cached'].includes(normalized)) {
    return 'completed';
  }

  if (normalized === 'skipped') {
    return 'skipped';
  }

  if (['running', 'start', 'started', 'pending'].includes(normalized)) {
    return 'running';
  }

  if (['error', 'failed', 'failure', 'stopped', 'cancelled', 'canceled', 'timeout'].includes(normalized)) {
    return 'error';
  }

  return 'completed';
};

/**
 * Hook for managing AI System execution via WebSocket
 */
export function useAISystemSocket(options: UseAISystemSocketOptions): UseAISystemSocketReturn {
  const {
    systemId,
    onConnect,
    onDisconnect,
    onMessage,
    onExecutionStarted,
    onExpertStarted,
    onExpertCompleted,
    onProgress,
    onExecutionCompleted,
    onExecutionError,
    autoConnect = true,
  } = options;

  // State
  const [isConnected, setIsConnected] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);
  const [currentExecutionId, setCurrentExecutionId] = useState<string | null>(null);
  const [executionStatus, setExecutionStatus] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [expertProgress, setExpertProgress] = useState<ExpertProgress[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [totalCost, setTotalCost] = useState(0);

  // Refs
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isConnectingRef = useRef(false); // Prevent concurrent connection attempts
  const reconnectAttemptsRef = useRef(0);
  const currentExecutionIdRef = useRef<string | null>(null);
  const executionWasActiveRef = useRef(false);
  const maxReconnectAttempts = 5;

  // Build WebSocket URL
  const getWebSocketUrl = useCallback(() => {
    const token = authService.getAccessToken();
    if (!token) {
      console.error('No auth token available for WebSocket connection');
      return null;
    }

    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = window.location.hostname;
    const isProduction = window.location.protocol === 'https:';
    const wsPort = isProduction ? '' : ':8000';
    
    return `${wsProtocol}//${wsHost}${wsPort}/ws/ai-systems/${systemId}/execution/?token=${token}`;
  }, [systemId]);

  // Handle incoming messages
  const handleMessage = useCallback((event: MessageEvent) => {
    try {
      const data: AISystemWSMessage = JSON.parse(event.data);
      console.log('[AISystemSocket] Message received:', data.type, data);

      // Call generic message handler
      onMessage?.(data);

      switch (data.type) {
        case 'connected':
          console.log(`Connected to AI System ${data.system_name} (ID: ${data.system_id})`);
          break;

        case 'execution_started':
          setCurrentExecutionId(data.execution_id || null);
          currentExecutionIdRef.current = data.execution_id || null;
          executionWasActiveRef.current = true;
          setIsExecuting(true);
          setExecutionStatus(data.status || 'START');
          setProgress(0);
          setExpertProgress([]);
          setError(null);
          setTotalCost(0);
          onExecutionStarted?.(data.execution_id!);
          break;

        case 'expert_started':
          setExpertProgress(prev => {
            // Check if expert already exists
            const exists = prev.some(e => e.expert_id === data.expert_id);
            if (exists) {
              return prev.map(e => 
                e.expert_id === data.expert_id 
                  ? { ...e, status: 'running' as const }
                  : e
              );
            }
            return [...prev, {
              expert_id: data.expert_id!,
              expert_name: data.expert_name!,
              expert_type: data.expert_type!,
              status: 'running' as const,
            }];
          });
          onExpertStarted?.(data.expert_id!, data.expert_name!);
          break;

        case 'expert_completed':
          setExpertProgress(prev => {
            const nextStatus = normalizeExpertStatus(data.status);
            const exists = prev.some(e => e.expert_id === data.expert_id);

            if (!exists) {
              return [...prev, {
                expert_id: data.expert_id!,
                expert_name: data.expert_name!,
                expert_type: data.expert_type || '',
                status: nextStatus,
                total_cost: data.total_cost,
                result: data.result,
              }];
            }

            return prev.map(e =>
              e.expert_id === data.expert_id
                ? {
                    ...e,
                    status: nextStatus,
                    total_cost: data.total_cost,
                    result: data.result,
                  }
                : e
            );
          });
          onExpertCompleted?.(data.expert_id!, data.expert_name!, data.status!, data.result);
          break;

        case 'expert_skipped':
          setExpertProgress(prev => {
            const exists = prev.some(e => e.expert_id === data.expert_id);
            if (exists) {
              return prev.map(e =>
                e.expert_id === data.expert_id
                  ? { ...e, status: 'skipped' as const }
                  : e
              );
            }
            return [...prev, {
              expert_id: data.expert_id!,
              expert_name: data.expert_name!,
              expert_type: data.expert_type || '',
              status: 'skipped' as const,
            }];
          });
          break;

        case 'execution_progress':
          setProgress(data.progress || 0);
          onProgress?.(data.progress || 0, data.experts_completed || 0, data.experts_total || 0);
          break;

        case 'execution_completed':
          currentExecutionIdRef.current = data.execution_id || currentExecutionIdRef.current;
          executionWasActiveRef.current = false;
          setIsExecuting(false);
          setExecutionStatus(data.status || 'COMPLETED');
          setProgress(100);
          setTotalCost(data.total_cost || 0);
          onExecutionCompleted?.(data.execution_id!, data.status!, data.result, data.total_cost);
          break;

        case 'execution_error':
          executionWasActiveRef.current = false;
          setIsExecuting(false);
          setExecutionStatus('ERROR');
          setError(data.error || 'Unknown error');
          onExecutionError?.(data.execution_id || currentExecutionId || '', data.error || 'Unknown error');
          break;

        case 'execution_status':
          // Full status update (from subscribe or get_status)
          setExecutionStatus(data.status || null);
          if ((data.status || '').toLowerCase() === 'running') {
            currentExecutionIdRef.current = data.execution_id || currentExecutionIdRef.current;
            executionWasActiveRef.current = true;
            setIsExecuting(true);
          } else if (['completed', 'success', 'error', 'failed', 'stopped', 'cancelled', 'canceled', 'timeout'].includes((data.status || '').toLowerCase())) {
            currentExecutionIdRef.current = data.execution_id || currentExecutionIdRef.current;
            executionWasActiveRef.current = false;
            setIsExecuting(false);
          }
          if (data.expert_results) {
            setExpertProgress(data.expert_results.map(r => ({
              expert_id: r.expert_id,
              expert_name: r.expert_name,
              expert_type: r.expert_type,
              status: normalizeExpertStatus(r.status),
              total_cost: r.total_cost,
            })));
          }
          break;

        case 'stop_requested':
          console.log('Stop requested:', data.message);
          break;

        case 'error':
          setError(data.error || 'Unknown error');
          console.error('[AISystemSocket] Error:', data.error);
          break;

        case 'pong':
          // Heartbeat response - connection is alive
          break;

        default:
          console.log('[AISystemSocket] Unknown message type:', data.type);
      }
    } catch (err) {
      console.error('[AISystemSocket] Failed to parse message:', err);
    }
  }, [currentExecutionId, onMessage, onExecutionStarted, onExpertStarted, onExpertCompleted, onProgress, onExecutionCompleted, onExecutionError]);

  // Connect to WebSocket
  const connect = useCallback(() => {
    // Don't connect if already connected, connecting, or a connection attempt is in progress
    if (
      wsRef.current?.readyState === WebSocket.OPEN ||
      wsRef.current?.readyState === WebSocket.CONNECTING ||
      isConnectingRef.current
    ) {
      console.log('[AISystemSocket] Skipping connection - already connected or connecting');
      return;
    }

    // Check max reconnect attempts
    if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
      console.log('[AISystemSocket] Max reconnect attempts reached, stopping');
      setError('Max reconnection attempts reached');
      return;
    }

    const wsUrl = getWebSocketUrl();
    if (!wsUrl) {
      setError('Authentication required');
      return;
    }

    isConnectingRef.current = true;
    console.log('[AISystemSocket] Connecting to:', wsUrl);
    
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log('[AISystemSocket] Connected');
      isConnectingRef.current = false;
      reconnectAttemptsRef.current = 0; // Reset on successful connection
      setIsConnected(true);
      setError(null);
      onConnect?.();

      // Start ping interval to keep connection alive
      pingIntervalRef.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'ping' }));
        }
      }, 30000); // Ping every 30 seconds

      const activeExecutionId = currentExecutionIdRef.current;
      if (activeExecutionId && executionWasActiveRef.current) {
        ws.send(JSON.stringify({
          type: 'subscribe',
          execution_id: activeExecutionId,
        }));
        ws.send(JSON.stringify({
          type: 'get_status',
          execution_id: activeExecutionId,
        }));
      }
    };

    ws.onmessage = handleMessage;

    ws.onerror = (event) => {
      console.error('[AISystemSocket] Error:', event);
      isConnectingRef.current = false;
      setError('WebSocket connection error');
    };

    ws.onclose = (event) => {
      console.log('[AISystemSocket] Disconnected:', event.code, event.reason);
      isConnectingRef.current = false;
      setIsConnected(false);
      wsRef.current = null;
      onDisconnect?.();

      // Clear ping interval
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }

      // Auto-reconnect with exponential backoff (unless intentionally closed)
      if (event.code === 4000) {
        // Server redeploying — reconnect immediately, skip backoff
        console.log('[AISystemSocket] Server redeploying, reconnecting immediately...');
        reconnectAttemptsRef.current = 0;
        reconnectTimeoutRef.current = setTimeout(() => {
          connect();
        }, 100);
      } else if (event.code !== 1000 && event.code !== 4001) {
        reconnectAttemptsRef.current += 1;
        const backoffDelay = Math.min(3000 * Math.pow(2, reconnectAttemptsRef.current - 1), 30000);
        console.log(`[AISystemSocket] Reconnecting in ${backoffDelay}ms (attempt ${reconnectAttemptsRef.current}/${maxReconnectAttempts})`);
        
        reconnectTimeoutRef.current = setTimeout(() => {
          connect();
        }, backoffDelay);
      }
    };
  }, [getWebSocketUrl, handleMessage, onConnect, onDisconnect]);

  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    // Reset connection tracking
    isConnectingRef.current = false;
    reconnectAttemptsRef.current = 0;

    // Clear reconnect timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    // Clear ping interval
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }

    // Close WebSocket
    if (wsRef.current) {
      wsRef.current.close(1000, 'Client disconnect');
      wsRef.current = null;
    }

    setIsConnected(false);
  }, []);

  // Start execution
  const startExecution = useCallback((metadata?: Record<string, any>, executionType?: string) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      setError('WebSocket not connected');
      return;
    }

    // Reset state
    setError(null);
    setProgress(0);
    setExpertProgress([]);
    setTotalCost(0);
    executionWasActiveRef.current = true;

    wsRef.current.send(JSON.stringify({
      type: 'execute',
      execution_type: executionType || 'MANUAL',
      metadata: metadata || {},
    }));
  }, []);

  // Stop execution
  const stopExecution = useCallback(() => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN || !currentExecutionId) {
      return;
    }

    wsRef.current.send(JSON.stringify({
      type: 'stop',
      execution_id: currentExecutionId,
    }));
  }, [currentExecutionId]);

  // Subscribe to existing execution
  const subscribeToExecution = useCallback((executionId: string) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      setError('WebSocket not connected');
      return;
    }

    setCurrentExecutionId(executionId);
    currentExecutionIdRef.current = executionId;
    executionWasActiveRef.current = true;
    wsRef.current.send(JSON.stringify({
      type: 'subscribe',
      execution_id: executionId,
    }));
  }, []);

  // Get execution status
  const getExecutionStatus = useCallback((executionId: string) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      return;
    }

    wsRef.current.send(JSON.stringify({
      type: 'get_status',
      execution_id: executionId,
    }));
  }, []);

  // Auto-connect on mount
  useEffect(() => {
    if (autoConnect) {
      // Debounce connection to avoid rapid reconnection attempts
      const connectionTimeout = setTimeout(() => {
        connect();
      }, 100);

      return () => {
        clearTimeout(connectionTimeout);
        disconnect();
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoConnect, systemId]); // Only depend on autoConnect and systemId, not connect/disconnect

  return {
    isConnected,
    isExecuting,
    currentExecutionId,
    executionStatus,
    progress,
    expertProgress,
    error,
    totalCost,
    startExecution,
    stopExecution,
    subscribeToExecution,
    getExecutionStatus,
    connect,
    disconnect,
  };
}

export default useAISystemSocket;
